import { Info } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const OVERLINE = "font-mono text-[10px] uppercase tracking-[0.14em] text-[#7b8799]";

/**
 * As ressalvas de um card, atrás de um ícone no canto do cabeçalho.
 *
 * Elas importam para quem analisa e, num ambiente de cliente bem coletado,
 * tendem a nunca aparecer: quando aparecem, em geral apontam outro defeito. Por
 * isso saem do corpo do card, onde empilhavam três caixas acima do gráfico, e
 * viram um ícone — mas com a **contagem visível**, porque nota escondida sem
 * sinal nenhum é o mesmo que nota apagada.
 *
 * Entra no slot `actions` do `Section`, que já posiciona no canto superior
 * direito.
 */
export function CardNotes({ testId, notes }: { testId: string; notes: string[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (notes.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          aria-label={t("Notes on the measurement")}
          className="flex h-6 items-center gap-1 rounded-md border border-[#e8ebf0] bg-white px-1.5 text-[#7b8799] hover:text-[#08254c]"
        >
          <Info aria-hidden className="h-3.5 w-3.5" />
          <span className="font-mono text-[10px] tabular-nums">{notes.length}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-3">
        <p className={cn(OVERLINE, "mb-2")}>{t("Notes on the measurement")}</p>
        <ul data-testid={`${testId}-content`} className="space-y-2">
          {notes.map((note) => (
            <li
              key={note}
              className="flex items-start gap-2 text-[11px] leading-snug text-[#596b85]"
            >
              <Info aria-hidden className="mt-px h-3.5 w-3.5 shrink-0 text-[#7b8799]" />
              <span>{note}</span>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
